import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FaEdit, FaTrash, FaSearch, FaBook, FaPlus, FaFilter, FaChevronLeft, FaChevronRight, FaEye, FaTimes, FaCheck, FaClock } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'
import { sendApiRequest } from '../utils/api'
import Swal from 'sweetalert2'
import LogoLoader from '../pages/LogoLoader'

const ExamList = () => {
  const navigate = useNavigate()
  const [exams, setExams] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(5)
  const [selectedExam, setSelectedExam] = useState(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingExam, setEditingExam] = useState(null)

  // Fetch exams
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true)
        const response = await sendApiRequest('api/admin/exams', {}, 'GET')
        if (response.status) {
          setExams(response.data)
        } else {
          setError(response.message || 'Failed to fetch exams')
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch exams')
      } finally {
        setLoading(false)
      }
    }

    fetchExams()
  }, [])

  // Filter exams based on search and status
  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === "active" ? exam.isActive : statusFilter === "inactive" ? !exam.isActive : true
    return matchesSearch && matchesStatus
  })

  // Pagination calculations
  const indexOfLastExam = currentPage * itemsPerPage
  const indexOfFirstExam = indexOfLastExam - itemsPerPage
  const currentExams = filteredExams.slice(indexOfFirstExam, indexOfLastExam)
  const totalPages = Math.ceil(filteredExams.length / itemsPerPage)

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4f46e5',
        cancelButtonColor: '#94a3b8',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        const response = await sendApiRequest(`api/admin/exams/${id}`, {}, 'DELETE')
        if (response.status) {
          setExams(exams.filter(exam => exam._id !== id))
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Exam has been deleted.',
            background: '#f8fafc',
            color: '#1e293b',
            confirmButtonColor: '#4f46e5'
          })
        } else {
          throw new Error(response.message || 'Failed to delete exam')
        }
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to delete exam',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  const handleView = (exam) => {
    setSelectedExam(exam)
    setIsViewModalOpen(true)
  }

  const handleEdit = (exam) => {
    setEditingExam(exam)
    setIsEditModalOpen(true)
  }

  const handleUpdateExam = async (e) => {
    e.preventDefault()
    try {
      const response = await sendApiRequest(`api/admin/exams/${editingExam._id}`, editingExam, 'PUT')
      if (response.status) {
        setExams(exams.map(exam =>
          exam._id === editingExam._id ? response.data : exam
        ))
        setIsEditModalOpen(false)
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Exam updated successfully',
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      } else {
        throw new Error(response.message || 'Failed to update exam')
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to update exam',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  const handleStatusToggle = async (exam) => {
    try {
      const updatedExam = {
        ...exam,
        isActive: !exam.isActive
      }

      const response = await sendApiRequest(`api/admin/exams/${exam._id}`, updatedExam, 'PUT')

      if (response.status) {
        setExams(exams.map(e =>
          e._id === exam._id ? response.data : e
        ))
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: `Exam ${response.data.isActive ? 'activated' : 'deactivated'} successfully`,
          background: '#f8fafc',
          color: '#1e293b',
          confirmButtonColor: '#4f46e5'
        })
      } else {
        throw new Error(response.message || 'Failed to update exam status')
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: err.message || 'Failed to update exam status',
        background: '#f8fafc',
        color: '#1e293b',
        confirmButtonColor: '#4f46e5'
      })
    }
  }

  if (loading) {
    return (
      <LogoLoader />
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 text-xl font-medium mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Exam Management</h1>
            <p className="text-slate-600">Organize and manage all your exams in one place</p>
          </div>
          <Link
            to="/admin-panel/exam-add"
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <FaPlus className="w-4 h-4" />
            Add New Exam
          </Link>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-6 rounded-xl mb-8 shadow-sm border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaSearch className="text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Search exams..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 text-slate-800 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaFilter className="text-slate-400" />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-slate-50 text-slate-800 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex items-center justify-end md:justify-start">
              <span className="text-sm text-slate-600">
                {filteredExams.length} {filteredExams.length === 1 ? 'exam' : 'exams'} found
              </span>
            </div>
          </div>
        </div>

        {/* Exams Table */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-slate-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr className="text-left">
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Title</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Description</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Batch Type</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Category</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Fee</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 font-medium text-slate-700 text-sm uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {currentExams.length > 0 ? (
                  currentExams.map((exam) => (
                    <motion.tr
                      key={exam._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-4">
                          {exam.thumbnail && (
                            <div className="relative overflow-hidden rounded-lg w-12 h-12 border border-slate-200">
                              <img
                                src={exam.thumbnail}
                                alt={exam.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <span className="text-slate-800 font-medium">{exam.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-slate-600 text-sm line-clamp-2 max-w-xs">
                          {exam.description}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-600 px-3 py-1 rounded-full bg-slate-100 text-sm">
                          {exam.batchType.split(' ')[0]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-600 px-3 py-1 rounded-full bg-slate-100 text-sm">
                          {exam.category.title}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <FaClock className="text-indigo-500" />
                          <span className="text-slate-800 font-medium">
                            {exam.timingPeriod} days
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-slate-800 font-medium">₹{exam.fee}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={exam.isActive}
                              onChange={() => handleStatusToggle(exam)}
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600">
                            </div>
                          </label>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${exam.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {exam.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-end space-x-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleView(exam)}
                            className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View"
                          >
                            <FaEye className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleEdit(exam)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <FaEdit className="w-4 h-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleDelete(exam._id)}
                            className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <FaTrash className="w-4 h-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <FaBook className="w-12 h-12 text-slate-300 mb-4" />
                        <p className="text-lg font-medium text-slate-700">No exams found</p>
                        <p className="text-sm text-slate-500">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredExams.length > 0 && (
          <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="text-slate-600 text-sm">
              Showing {indexOfFirstExam + 1} to {Math.min(indexOfLastExam, filteredExams.length)} of {filteredExams.length} entries
            </div>
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-slate-700 bg-white rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="w-4 h-4" />
              </motion.button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'} transition-colors border border-slate-200`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-slate-700 bg-white rounded-lg hover:bg-slate-50 transition-colors border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        <AnimatePresence>
          {isEditModalOpen && editingExam && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-6 border-b border-slate-200 mt-20">
                  <h2 className="text-2xl font-bold text-slate-800">Edit Exam</h2>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={() => setIsEditModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <FaTimes className="w-5 h-5" />
                  </motion.button>
                </div>

                <form onSubmit={handleUpdateExam} className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Exam Name
                    </label>
                    <input
                      type="text"
                      value={editingExam.name}
                      onChange={(e) => setEditingExam({
                        ...editingExam,
                        name: e.target.value
                      })}
                      className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={editingExam.description}
                      onChange={(e) => setEditingExam({
                        ...editingExam,
                        description: e.target.value
                      })}
                      rows="3"
                      className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Exam Category
                      </label>
                      <select
                        value={editingExam.category ? editingExam.category._id : ''}
                        onChange={(e) => setEditingExam({
                          ...editingExam,
                          category: e.target.value
                        })}
                        className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      >
                        <option value="">Select Category</option>
                        {exams.map((exam) => (
                          <option key={exam._id} value={exam.category ? exam.category._id : ''}>
                            {exam.category ? exam.category.title : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Batch Type
                      </label>
                      <select
                        value={editingExam.batchType}
                        onChange={(e) => setEditingExam({
                          ...editingExam,
                          batchType: e.target.value
                        })}
                        className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      >
                        <option value="Regular Batch">Regular Batch</option>
                        <option value="Weekend Batch">Weekend Batch</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Duration (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={editingExam.timingPeriod}
                        onChange={(e) => setEditingExam({
                          ...editingExam,
                          timingPeriod: parseInt(e.target.value)
                        })}
                        className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Fee (₹)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={editingExam.fee}
                        onChange={(e) => setEditingExam({
                          ...editingExam,
                          fee: e.target.value
                        })}
                        className="w-full px-4 py-3 bg-white text-slate-800 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Status
                      </label>
                      <div className="flex items-center mt-2">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={editingExam.isActive}
                            onChange={(e) => setEditingExam({
                              ...editingExam,
                              isActive: e.target.checked
                            })}
                          />
                          <div className="relative w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600">
                          </div>
                          <span className="ml-3 text-sm font-medium text-slate-700">
                            {editingExam.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsEditModalOpen(false)}
                      className="px-6 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                      <FaCheck className="w-4 h-4" />
                      Update Exam
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* View Modal */}
        <AnimatePresence>
          {isViewModalOpen && selectedExam && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
              >
                <div className="flex justify-between items-center p-6 border-b border-slate-200">
                  <h2 className="text-2xl font-bold text-slate-800">Exam Details</h2>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    onClick={() => setIsViewModalOpen(false)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <FaTimes className="w-5 h-5" />
                  </motion.button>
                </div>

                <div className="overflow-y-auto max-h-[70vh] p-6 space-y-6">
                  {selectedExam.thumbnail && (
                    <div className="rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                      <img
                        src={selectedExam.thumbnail}
                        alt={selectedExam.name}
                        className="w-full h-48 object-cover"
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Exam Name</h3>
                      <p className="text-lg font-semibold text-slate-800">{selectedExam.name}</p>
                    </div>

                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Exam Category</h3>
                      <p className="text-lg font-semibold text-slate-800">{selectedExam.category?.title}</p>
                    </div>
                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Batch Type</h3>
                      <p className="text-lg font-semibold text-slate-800">{selectedExam.batchType}</p>
                    </div>

                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Duration</h3>
                      <div className="flex items-center gap-2">
                        <FaClock className="text-indigo-500" />
                        <p className="text-lg font-semibold text-slate-800">{selectedExam.timingPeriod} days</p>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Fee</h3>
                      <p className="text-lg font-semibold text-slate-800">₹{selectedExam.fee}</p>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Description</h3>
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedExam.description}</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">Status:</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${selectedExam.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                      }`}>
                      {selectedExam.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-200 flex justify-end">
                  <button
                    onClick={() => setIsViewModalOpen(false)}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default ExamList